import * as React from "react";

import { cn } from "@borderless/ui/lib/utils";
import { cva, VariantProps } from "class-variance-authority";

export const cardClassName = "border-border rounded-xl border p-6";

export const cardVariants = cva(cardClassName, {
  variants: {
    variant: {
      default: "bg-background",
      button:
        "text-tertiary-foreground bg-tertiary border shadow-[4px_4px_0_var(--tertiary-shadow)] transition-all duration-100 ease-out hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0_var(--tertiary-shadow)] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none data-[state=active]:translate-x-[4px] data-[state=active]:translate-y-[4px] data-[state=active]:shadow-none",
      "button-bg":
        "text-foreground bg-background border-shadow border shadow-[4px_4px_0_var(--card-shadow)] transition-all duration-100 ease-out hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0_var(--card-shadow)] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none data-[state=active]:translate-x-[4px] data-[state=active]:translate-y-[4px] data-[state=active]:shadow-none",
      "no-border": "border-none",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

function Card({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof cardVariants>) {
  return (
    <div
      data-slot="card"
      className={cn(cardVariants({ variant, className }))}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("leading-none font-semibold", className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-6", className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      {...props}
    />
  );
}

export {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
};
